import { useCallback, useEffect, useMemo, useState } from "react";
import { useLazyFetchTableValueQuery } from "../../../api/rtk/base/slices/tablesApiSlice";
import { useLazyFetchPathsQuery } from "../../../api/rtk/base/slices/pathsApiSlice";
import { getPaths, getRoadRaionSegments } from "../../../widgets/loginForm/loginFormFunctions";
import { useAppSelector } from "../../../stateManagement/hooks/redux";
import { getItemWebStorage } from "../../../features/webStorageSaver";
import {
  buildRaionItems,
  buildRoadItems,
  contiguityFactor,
  groupSegmentsByPath,
  Path,
  RaionItem,
  RoadItem,
} from "./utils";

const W_WORD = 1;
const W_CONTIG = 50;
const W_EXACT = 20;

interface RaionRoadGroup {
  roadId: string;
  roadName: string;
  paths: Path[];
}

export type GroupedItem = RaionItem | RoadItem;

export function useGroupPaths() {
  /* --------------------------- external state --------------------------- */
  const currentConfig = getItemWebStorage("currentConfig");
  const { roadListIsGroupedByRaions } = currentConfig ?? {};
  const { pathsList, pathsSearchValue, roadRaionSegments } = useAppSelector(
    (state) => state.pathsInfo
  );

  /* ------------------------- local loading state ------------------------ */
  const [isFetchingPaths, setIsFetchingPaths] = useState<boolean>(false);
  const [isFetchingRoadRaionSegments, setIsFetchingRoadRaionSegments] =
    useState<boolean>(false);

  /* ------------------------------ queries ------------------------------- */
  const [fetchPaths] = useLazyFetchPathsQuery();
  const [fetchTableValue] = useLazyFetchTableValueQuery();

  /* -------------------------- data bootstrap --------------------------- */
  useEffect(() => {
    if (!pathsList) {
      initMissedPath();
    }
  }, [pathsList]);

  useEffect(() => {
    if (!roadRaionSegments && roadListIsGroupedByRaions) {
      initMissedRoadRaionSegments();
    }
  }, [roadRaionSegments]);

  const initMissedPath = useCallback(async () => {
    setIsFetchingPaths(true);
    await getPaths(fetchPaths);
    setIsFetchingPaths(false);
  }, [fetchPaths]);

  const initMissedRoadRaionSegments = useCallback(async () => {
    setIsFetchingRoadRaionSegments(true);
    await getRoadRaionSegments(() =>
      fetchTableValue({ tableValueUrl: "objects/RoadRaionSegments" })
    );
    setIsFetchingRoadRaionSegments(false);
  }, [fetchTableValue]);

  /* ------------------------------ helpers ------------------------------- */
  const alphabeticalRoadSort = (
    a: { roadName: string },
    b: { roadName: string }
  ) => a.roadName.localeCompare(b.roadName, "ru");

  /* --------------------------- transformations --------------------------- */
  // 1) сегменты → Map
  const segMap = useMemo(() => groupSegmentsByPath(roadRaionSegments ?? []), [
    roadRaionSegments,
  ]);

  // 2) строим два списка: реальных районов и «дорог без района»
  const raionItems: RaionItem[] = useMemo(
    () => (pathsList ? buildRaionItems(pathsList, segMap) : []),
    [pathsList, segMap]
  );

  const roadItems: RoadItem[] = useMemo(
    () => (pathsList ? buildRoadItems(pathsList, segMap) : []),
    [pathsList, segMap]
  );

  // 3) объединяем в один массив для скорости работы поиска
  const searchable: GroupedItem[] = useMemo(
    () => [...raionItems, ...roadItems],
    [raionItems, roadItems]
  );

  // 4) фильтрация / сортировка
  const filtered: GroupedItem[] = useMemo(() => {
    const raw = pathsSearchValue?.trim();
    const q = raw ? raw.toLowerCase() : "";

    /* --------------------------------------------------------------- */
    /*  Без поиска → алфавит + приоритет районов                       */
    /* --------------------------------------------------------------- */
    if (!q) {
      const sortedRaions: RaionItem[] = [...raionItems]
        .map((ri) => ({
          ...ri,
          roads: [...ri.roads].sort(alphabeticalRoadSort),
        }))
        .sort((a, b) => a.raionName.localeCompare(b.raionName, "ru"));

      const sortedRoads: RoadItem[] = [...roadItems].sort(alphabeticalRoadSort);

      // Районы всегда первыми, затем одиночные дороги
      return [...sortedRaions, ...sortedRoads];
    }

    /* --------------------------------------------------------------- */
    /*  С поиском → старый скоринг + фильтр                             */
    /* --------------------------------------------------------------- */
    const words = q.split(/\s+/);

    return searchable
      .map((item) => {
        // Для RaionItem: пробегаем по всем roads, для RoadItem — по единственной
        const groups =
          item.type === "raion"
            ? item.roads
            : [{ roadId: item.roadId, roadName: item.roadName, paths: item.paths }];

        const metas = groups
          .map((g) => {
            const text = [
              (item as any).raionName,
              g.roadName,
              ...g.paths.map((p) => p.Name!),
            ]
              .join(" ")
              .toLowerCase();
            const m = words.reduce((c, w) => (text.includes(w) ? c + 1 : c), 0);
            if (!m) return null;
            const cf = contiguityFactor(text, words);
            const exact = text.includes(q) ? W_EXACT : 0;
            return { g, score: m * W_WORD + cf * W_CONTIG + exact };
          })
          .filter((x): x is { g: RaionRoadGroup; score: number } => !!x)
          .sort((a, b) => b.score - a.score);

        if (!metas.length) return null;

        return {
          ...item,
          roads: item.type === "raion" ? metas.map((m) => m.g) : item.roads,
          _score: metas[0].score,
        } as GroupedItem & { _score: number };
      })
      .filter((x): x is GroupedItem & { _score: number } => !!x)
      .sort((a, b) => b._score - a._score)
      .map(({ _score, ...rest }) => rest as GroupedItem);
  }, [raionItems, roadItems, searchable, pathsSearchValue]);

  /* ------------------------------- output ------------------------------- */
  return {
    pathsGroups: filtered,
    isLoading: isFetchingPaths || isFetchingRoadRaionSegments,
  };
}
